import { CommonModule } from "@angular/common";
import { PAGE_SIZE_OPTIONS } from "../../../shared/constants/page-size.constants";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import { Devise } from "../../../services/generated/waba-api/models/devise";
import { DeviseApiService } from "../../../services/generated/waba-api/services/devise-api.service";
import { AlertService } from "../../../shared/alert/service/alert.service";

@Component({
  selector: "app-devise-manage-panel",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule],
  templateUrl: "./devise-manage-panel.component.html",
  styleUrl: "./devise-manage-panel.component.scss",
})
export class DeviseManagePanelComponent implements OnInit, OnChanges {
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  isRTL = false;
  currentView: "list" | "form" = "list";

  // ── List state ──
  devises: Devise[] = [];
  filteredDevises: Devise[] = [];
  paginatedDevises: Devise[] = [];
  isLoading = false;
  searchTerm = "";
  totalElements = 0;
  currentPage = 0;
  pageSize = 10;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  // ── Form state ──
  deviseForm!: FormGroup;
  isEditMode = false;
  editingReference: string | null = null;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private deviseService: DeviseApiService,
    private alertService: AlertService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.isRTL = document.documentElement.getAttribute("dir") === "rtl";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["open"] && this.open) {
      this.currentView = "list";
      this.searchTerm = "";
      this.currentPage = 0;
      this.loadDevises();
    }
  }

  onClose(): void {
    this.currentView = "list";
    this.closed.emit();
  }

  // ══════════════════════════════════════════
  //  LIST VIEW
  // ══════════════════════════════════════════

  loadDevises(): void {
    this.isLoading = true;
    this.deviseService
      .searchDevise({
        body: {
          page: 0,
          size: 500,
          sort: "ASC",
          filter: { code: undefined, libelle: undefined, archived: null } as any,
        },
        "x-api-canal": "web",
        "x-api-requestId": uuidv4(),
      })
      .subscribe({
        next: (response) => {
          this.devises = response?.body?.entities ?? [];
          this.applyFilter();
          this.isLoading = false;
        },
        error: () => {
          this.devises = [];
          this.filteredDevises = [];
          this.paginatedDevises = [];
          this.totalElements = 0;
          this.isLoading = false;
        },
      });
  }

  onSearchChange(): void {
    this.currentPage = 0;
    this.applyFilter();
  }

  private applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    this.filteredDevises = term
      ? this.devises.filter(
          (d) =>
            (d.code ?? "").toLowerCase().includes(term) ||
            (d.libelle ?? "").toLowerCase().includes(term),
        )
      : [...this.devises];
    this.totalElements = this.filteredDevises.length;
    this.applyPagination();
  }

  private applyPagination(): void {
    const start = this.currentPage * this.pageSize;
    this.paginatedDevises = this.filteredDevises.slice(
      start,
      start + this.pageSize,
    );
  }

  get rangeStart(): number {
    return this.totalElements === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.totalElements);
  }

  get canGoPrevious(): boolean {
    return this.currentPage > 0;
  }

  get canGoNext(): boolean {
    return (this.currentPage + 1) * this.pageSize < this.totalElements;
  }

  goPrevious(): void {
    if (this.canGoPrevious) {
      this.currentPage--;
      this.applyPagination();
    }
  }

  goNext(): void {
    if (this.canGoNext) {
      this.currentPage++;
      this.applyPagination();
    }
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.currentPage = 0;
    this.applyPagination();
  }

  // ── Archive toggle ──
  toggleArchive(devise: Devise): void {
    this.deviseService
      .patchDeviseArchiveStatus({
        ref: devise.reference!,
        "x-api-canal": "web",
        "x-api-requestId": uuidv4(),
      })
      .subscribe({
        next: () => {
          this.alertService.showSuccess(
            this.translateService.instant("APP_SWAL.SUCCESS_OPERATION"),
          );
          this.loadDevises();
        },
        error: () => {
          this.alertService.showError(
            this.translateService.instant("APP_SWAL.ERROR"),
          );
        },
      });
  }

  // ── Delete ──
  deleteDevise(devise: Devise): void {
    Swal.fire({
      showCloseButton: true,
      title: this.translateService.instant("APP_SWAL.CONFIRM_DELETE"),
      text: this.translateService.instant("APP_SWAL.WARNING"),
      icon: "warning",
      showCancelButton: true,
      iconColor: "#00908e",
      confirmButtonColor: "#00908e",
      cancelButtonColor: "#094259",
      confirmButtonText: this.translateService.instant("APP_SWAL.YES"),
      cancelButtonText: this.translateService.instant("APP_SWAL.NO"),
      customClass: {
        confirmButton: "btn-confirm-large",
        cancelButton: "btn-confirm-large",
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.deviseService
        .deleteDeviseByRef({
          ref: devise.reference!,
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
        })
        .subscribe({
          next: () => {
            this.alertService.showSuccess(
              this.translateService.instant("APP_SWAL.SUCCESS_OPERATION"),
            );
            this.loadDevises();
          },
          error: () => {
            this.alertService.showError(
              this.translateService.instant("APP_SWAL.ERROR"),
            );
          },
        });
    });
  }

  // ══════════════════════════════════════════
  //  FORM VIEW
  // ══════════════════════════════════════════

  private initForm(): void {
    this.deviseForm = this.fb.group({
      code: ["", Validators.required],
      libelle: ["", Validators.required],
    });
  }

  openCreateForm(): void {
    // Don't keep the list loader state — otherwise the create form only
    // shows a spinner and fields never appear until search finishes.
    this.isLoading = false;
    this.initForm();
    this.isEditMode = false;
    this.editingReference = null;
    this.currentView = "form";
  }

  openEditForm(devise: Devise): void {
    this.initForm();
    this.isEditMode = true;
    this.editingReference = devise.reference ?? null;
    this.currentView = "form";
    if (devise.reference) {
      this.loadDeviseForEdit(devise.reference);
    }
  }

  private isApiSuccess(res: { status?: string | number } | null | undefined): boolean {
    return res != null && String(res.status) === "200";
  }

  backToList(): void {
    this.currentView = "list";
  }

  private loadDeviseForEdit(ref: string): void {
    this.isLoading = true;
    this.deviseService
      .getDeviseByRef({
        ref,
        "x-api-canal": "web",
        "x-api-requestId": uuidv4(),
      })
      .subscribe({
        next: (response) => {
          if (response?.body) {
            this.deviseForm.patchValue(response.body);
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.alertService.showError(
            this.translateService.instant("APP_GENERIC_ALERTS.LOAD_FAILED"),
          );
          this.backToList();
        },
      });
  }

  submitForm(): void {
    if (this.deviseForm.invalid) {
      this.alertService.showError(
        this.translateService.instant("APP_GENERIC_ALERTS.REQUIRED_FIELDS"),
      );
      this.deviseForm.markAllAsTouched();
      return;
    }

    const raw = this.deviseForm.value;
    // Align with referential devise form (status: 1) + normalize code.
    const body = {
      code: String(raw.code ?? "")
        .trim()
        .toUpperCase(),
      libelle: String(raw.libelle ?? "").trim(),
      status: 1,
    } as Devise;
    this.isSaving = true;

    if (this.isEditMode && this.editingReference) {
      this.deviseService
        .updateDevise({
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
          body,
          ref: this.editingReference,
        })
        .subscribe({
          next: (res) => {
            this.isSaving = false;
            if (this.isApiSuccess(res)) {
              this.alertService.showSuccess(
                this.translateService.instant(
                  "APP_GENERIC_ALERTS.UPDATED_SUCCESS",
                ),
              );
              this.saved.emit();
              this.loadDevises();
              this.backToList();
            } else {
              this.alertService.showError(
                res?.message ||
                  this.translateService.instant(
                    "APP_GENERIC_ALERTS.UPDATE_FAILED",
                  ),
              );
            }
          },
          error: (err) => {
            this.isSaving = false;
            this.alertService.showError(
              err?.error?.message ||
                this.translateService.instant(
                  "APP_GENERIC_ALERTS.UPDATE_FAILED",
                ),
            );
          },
        });
    } else {
      this.deviseService
        .createDevise({
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
          body,
        })
        .subscribe({
          next: (res) => {
            this.isSaving = false;
            // API may return status "200" without a body — still treat as success.
            if (this.isApiSuccess(res)) {
              this.alertService.showSuccess(
                this.translateService.instant(
                  "APP_GENERIC_ALERTS.CREATED_SUCCESS",
                ),
              );
              this.saved.emit();
              this.loadDevises();
              this.backToList();
            } else {
              this.alertService.showError(
                res?.message ||
                  this.translateService.instant(
                    "APP_GENERIC_ALERTS.CREATION_FAILED",
                  ),
              );
            }
          },
          error: (err) => {
            this.isSaving = false;
            this.alertService.showError(
              err?.error?.message ||
                this.translateService.instant(
                  "APP_GENERIC_ALERTS.CREATION_FAILED",
                ),
            );
          },
        });
    }
  }
}
